import './SocialSetup.scss';

import { FC, useContext, useEffect } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useDataSet } from 'customHooks/useDataSet';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import DataSetOperators from 'interfaces/DataSetOperators';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { SocialAccount } from 'models/Social/SocialAccount';
import { SocialSubAccount } from 'models/Social/SocialSubAccount';
import { Col, Row } from 'react-bootstrap';

import FacebookAuth from '../FacebookAuth/FacebookAuth';
import LinkedinAuth from '../LinkedinAuth/LinkedinAuth';
import SocialAccountList from '../SocialAccountList/SocialAccountList';
import TwitterAuth from '../TwitterAuth/TwitterAuth';


// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface SocialSetupProps { }

const SocialSetup: FC<SocialSetupProps> = () => {
  const onTokenExpired = useTokenExpiredHandler();
  const { configRepo, notify, selectedProjectId, projects, sendLog } = useContext(AppContext);
  const { dataSet, setOperators, operators } = useDataSet<SocialAccount>(
    configRepo!.PUBLISH_API_BASE_URI,
    Entities.SocialAccounts,
    selectedProjectId,
  );
  const socialAccounts: SocialAccount[] = dataSet.responseData as SocialAccount[];
  const { createEntity, updateEntity } = useModifyEntityService<SocialAccount>(
    configRepo!.PUBLISH_API_BASE_URI,
    Entities.SocialAccounts,
    sendLog,
    onTokenExpired,
  );

  const subAccountParams: DataSetOperators = {
    Top: 100,
  };
  const { dataSet: subAccountsDataSet, setOperators: setSubAccountsQueryOperators } =
    useDataSet<SocialSubAccount>(
      configRepo!.PUBLISH_API_BASE_URI,
      Entities.SocialSubAccounts,
      selectedProjectId,
      subAccountParams,
    );
  const subAccounts: SocialSubAccount[] = subAccountsDataSet.responseData as SocialSubAccount[];

  const { updateEntity: setSubAccount } = useModifyEntityService<SocialSubAccount>(
    configRepo!.PUBLISH_API_BASE_URI,
    Entities.SocialSubAccounts,
    sendLog,
    onTokenExpired,
  );

  let selectedProject = projects.find((p) => p.id === selectedProjectId);
  useEffect(() => {
    if (selectedProjectId !== operators.ProjectId) {
      const ops = { ...operators };
      ops.ProjectId = selectedProjectId;
      setOperators(ops);

      const subAcctOps = { ...subAccountParams };
      subAcctOps.ProjectId = selectedProjectId;
      setSubAccountsQueryOperators(subAcctOps);

      selectedProject = projects.find((p) => p.id === selectedProjectId);

    }
  }, [selectedProjectId]);

  const onSubAccountChanged = async (selectedSubAccount: SocialSubAccount) => {
    await setSubAccount(selectedSubAccount);
    const ops = { ...operators };
    setOperators(ops);
  };

  const onTokenAcquired = async (account: SocialAccount) => {
    const existingConnection = socialAccounts.find(
      (s) => s.connectionType === account.connectionType,
    );
    let result;
    if (existingConnection) {
      const updatedAccount = {
        ...existingConnection,
        ...account,
      };
      result = await updateEntity(updatedAccount);
    } else {
      result = await createEntity(account);
    }

    if (result) {
      const ops = { ...operators };
      setOperators(ops);
      const subAcctOps = { ...subAccountParams };
      subAcctOps.ProjectId = account.projectId;
      setSubAccountsQueryOperators(subAcctOps);
      notify(
        `${account.connectionType} account has been updated successfully.`,
        NotificationType.Success,
      );
    } else {
      sendLog(`failed to update social account with: ${result}`, LogLevel.error);
      notify(`${account.connectionType} failed to update.`, NotificationType.Error);
    }
  };
  if (dataSet.isLoading) {
    return <span>Loading...</span>;
  }
  if (dataSet.isError) {
    sendLog(`error loading dataset`, LogLevel.error, dataSet.error);
    return <span>Error loading dataset. Please try refreshing the page or contact support.</span>;
  }

  const heading: NavHeadingProps = {
    title: `Connect to Your Social Media Accounts`,
    ...NavHeadings.SocialSetup,
  };


  return (
    <div className="social-setup">
      <NavHeading {...heading} />
      <Row className="social-account-connectors my-4">
        <Col>
          <h3>Facebook</h3>
          <p>To authenticate or reauthenticate to Facebook, click the button below.</p>
          <FacebookAuth onTokenAcquired={onTokenAcquired} />
        </Col>
        <Col>
          <h3>LinkedIn</h3>
          <p>To authenticate or reauthenticate to LinkedIn, click the button below.</p>
          <LinkedinAuth onTokenAcquired={onTokenAcquired} />
        </Col>
        <Col>
          <h3>X</h3>
          <p>To authenticate or reauthenticate to X, click the button below.</p>
          <TwitterAuth onTokenAcquired={onTokenAcquired} />
        </Col>
        {/* <Col>
          <h3>Google</h3>
          <p>To authenticate or reauthenticate to Google, click the button below.</p>
          <GoogleAuth onTokenAcquired={onTokenAcquired} />
        </Col> */}
      </Row>

      <SocialAccountList
        socialAccounts={socialAccounts}
        subAccounts={subAccounts}
        projectName={selectedProject?.name}
        onSubAccountChanged={onSubAccountChanged}
      />
    </div>
  );
};

export default SocialSetup;
